import { Component } from '@theme/component';

/**
 * @typedef {Object} AxAthletesRefs
 * @property {HTMLElement & { swiper?: any; initialize?: () => void }} swiper
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 */

/**
 * Athletes section — carrousel Swiper, un seul chemin de rendu.
 *
 * Le nombre de tuiles visibles est piloté par les réglages `columns` (desktop) et
 * `columns_mobile`. Les flèches ne sont rendues (Liquid) que si le nombre de blocs
 * dépasse `columns` : en deçà tout tient à l'écran, le glissement est donc désactivé
 * en desktop pour que la rangée se comporte comme une grille statique.
 *
 * Le mode « horizontal » (grille 2x2 desktop + déplacement des cartes entre <ul> et
 * carrousel selon le breakpoint) a été retiré : il ne figurait plus dans la maquette
 * et n'était utilisé par aucune instance.
 *
 * @extends {Component<AxAthletesRefs>}
 */
class AxAthletes extends Component {
  async connectedCallback() {
    super.connectedCallback();

    await customElements.whenDefined('swiper-container');
    if (!this.isConnected) return;

    this.#initSwiper();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const swiper = this.refs.swiper?.swiper;
    if (swiper && !swiper.destroyed) swiper.destroy(true, true);
  }

  previous() {
    this.refs.swiper?.swiper?.slidePrev();
  }

  next() {
    this.refs.swiper?.swiper?.slideNext();
  }

  #initSwiper() {
    const swiperEl = this.refs.swiper;
    if (!swiperEl) return;

    const columns = Number(this.dataset.columns) || 4;
    const isCarousel = this.dataset.carousel === 'true';

    Object.assign(swiperEl, {
      slidesPerView: 'auto',
      spaceBetween: 8,
      grabCursor: true,
      a11y: { enabled: true },
      watchOverflow: true,
      breakpoints: {
        1200: {
          slidesPerView: columns,
          spaceBetween: 12,
          allowTouchMove: isCarousel,
          grabCursor: isCarousel,
        },
      },
    });
    swiperEl.initialize();
    swiperEl.removeAttribute('init');

    if (isCarousel) this.#syncArrows();
  }

  /** Grise les flèches en début et en fin de course. */
  #syncArrows() {
    const swiper = this.refs.swiper?.swiper;
    const { previous, next } = this.refs;
    if (!swiper || (!previous && !next)) return;

    const sync = () => {
      if (previous) previous.disabled = Boolean(swiper.isBeginning);
      if (next) next.disabled = Boolean(swiper.isEnd);
    };
    sync();

    for (const event of ['slideChange', 'reachBeginning', 'reachEnd', 'fromEdge', 'resize']) {
      swiper.on(event, sync);
    }
  }
}

customElements.define('ax-athletes', AxAthletes);
